// CredentialRequestModal.js
import React, { useState, useRef, useEffect } from 'react';
import { X, ArrowRight, CheckCircle2, ChevronDown, Check } from 'lucide-react';
import emailjs from '@emailjs/browser';
import logo from '../imgs/Logo_Fcn.png';
import { EMAIL_CONFIG } from '../config/emailConfig';

// Mismo sistema de campos que Login.js
const LABEL_CLASS = 'block text-center text-xs font-medium uppercase tracking-wide text-neutral-300 mb-2.5';
const INPUT_CLASS =
  'w-full px-0 py-2.5 bg-transparent border-0 border-b border-neutral-100 text-[15px] text-cafe-800 placeholder:text-neutral-200 focus:outline-none focus:border-b-2 focus:border-cafe-700 transition-all duration-200';

const UNITS = [
  'Dirección General',
  'Recursos Humanos',
  'Ingeniería',
  'Contabilidad',
  'Administración',
  'Mantenimiento',
  'Mercadeo',
  'Auditoría',
  'Legal',
  'Otro',
];

const CredentialRequestModal = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    unit: '',
    position: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Desplegable de unidad
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitError, setUnitError] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const unitRef = useRef(null);
  const unitButtonRef = useRef(null);
  const unitListRef = useRef(null);

  useEffect(() => {
    if (!unitOpen) return undefined;
    const handleClickOutside = (e) => {
      if (unitRef.current && !unitRef.current.contains(e.target)) setUnitOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [unitOpen]);

  useEffect(() => {
    if (unitOpen && unitListRef.current) {
      const item = unitListRef.current.children[activeIndex];
      if (item) item.scrollIntoView({ block: 'nearest' });
    }
  }, [unitOpen, activeIndex]);

  const openUnit = () => {
    setActiveIndex(Math.max(UNITS.indexOf(formData.unit), 0));
    setUnitOpen(true);
  };

  const selectUnit = (unit) => {
    setFormData(prev => ({ ...prev, unit }));
    setUnitError(false);
    setUnitOpen(false);
  };

  const handleUnitKeyDown = (e) => {
    if (e.key === 'Escape' && unitOpen) {
      e.stopPropagation();
      setUnitOpen(false);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!unitOpen) {
        openUnit();
      } else {
        setActiveIndex(i => (e.key === 'ArrowDown' ? Math.min(i + 1, UNITS.length - 1) : Math.max(i - 1, 0)));
      }
    } else if (e.key === 'Enter' && unitOpen) {
      e.preventDefault();
      selectUnit(UNITS[activeIndex]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.unit) {
      setUnitError(true);
      unitButtonRef.current?.focus();
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar datos exactos del template
      const templateParams = {
        name: formData.name,
        unit: formData.unit,
        position: formData.position,
        email: formData.email,
        Employee_id: `EMP${Date.now().toString().slice(-6)}`,
        request_date: new Date().toLocaleDateString('es-CO'),
        request_time: new Date().toLocaleTimeString('es-CO')
      };

      // Enviar con EmailJS usando tu configuración
      await emailjs.send(
        EMAIL_CONFIG.SERVICE_ID,
        EMAIL_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAIL_CONFIG.PUBLIC_KEY
      );

      setSubmitted(true);

      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setFormData({ name: '', unit: '', position: '', email: '' });
      }, 3000);

    } catch (error) {
      console.error('Error:', error);
      alert(`Error al enviar: ${error.text || error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-cafe-800 bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white text-left rounded-xl shadow-[0_40px_90px_-25px_rgba(59,39,25,0.4)] w-full max-w-xl max-h-[92vh] overflow-y-auto animate-card-in">
        <div className="relative p-6 sm:p-10">
          <div className="flex justify-center mb-8">
            <img src={logo} alt="Logo Fondo Cafetero" className="h-16" />
          </div>

          {!submitted ? (
            <>
              <button
                onClick={onClose}
                aria-label="Cerrar"
                className="absolute top-5 right-5 text-neutral-200 hover:text-neutral-400 transition-colors duration-200 disabled:opacity-40"
                disabled={isSubmitting}
              >
                <X className="h-5 w-5" />
              </button>

              <div className="mb-10 text-center">
                <h2 className="text-2xl font-bold text-cafe-800 mb-2 tracking-tight">
                  Solicitud de credenciales
                </h2>
                <p className="text-sm text-neutral-300">
                  Complete sus datos para solicitar el acceso.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-7">
                <div className="group">
                  <label htmlFor="cred-name" className={LABEL_CLASS}>
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    id="cred-name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className={INPUT_CLASS}
                  />
                </div>

                <div className="group">
                  <label htmlFor="cred-email" className={LABEL_CLASS}>
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    id="cred-email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="nombre@fondocafetero.com"
                    className={INPUT_CLASS}
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-7">
                  <div className="group">
                    <label htmlFor="cred-position" className={LABEL_CLASS}>
                      Puesto de trabajo
                    </label>
                    <input
                      type="text"
                      id="cred-position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      placeholder="Ej: Analista de Sistemas"
                      className={INPUT_CLASS}
                    />
                  </div>

                  <div className="group">
                    <label htmlFor="cred-unit" className={LABEL_CLASS}>
                      Unidad / Departamento
                    </label>
                    <div className="relative" ref={unitRef}>
                      <button
                        type="button"
                        id="cred-unit"
                        ref={unitButtonRef}
                        onClick={() => (unitOpen ? setUnitOpen(false) : openUnit())}
                        onKeyDown={handleUnitKeyDown}
                        aria-haspopup="listbox"
                        aria-expanded={unitOpen}
                        className={`${INPUT_CLASS} block text-left pr-8 ${unitOpen ? 'border-b-2 border-cafe-700' : ''} ${unitError ? 'border-red-400' : ''}`}
                      >
                        <span className={`block truncate ${formData.unit ? '' : 'text-neutral-200'}`}>
                          {formData.unit || 'Seleccione una unidad'}
                        </span>
                      </button>
                      <ChevronDown
                        className={`absolute right-0 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-200 pointer-events-none transition-transform duration-200 ${unitOpen ? 'rotate-180' : ''}`}
                      />

                      {unitOpen && (
                        <ul
                          ref={unitListRef}
                          role="listbox"
                          className="absolute z-20 left-0 right-0 bottom-full mb-2 max-h-60 overflow-y-auto bg-white rounded-lg border border-neutral-50 shadow-[0_18px_40px_-12px_rgba(59,39,25,0.3)] py-1.5"
                        >
                          {UNITS.map((unit, i) => {
                            const selected = formData.unit === unit;
                            return (
                              <li
                                key={unit}
                                role="option"
                                aria-selected={selected}
                                onMouseEnter={() => setActiveIndex(i)}
                                onClick={() => selectUnit(unit)}
                                className={`flex items-center justify-between px-4 py-2.5 text-sm cursor-pointer transition-colors duration-150 ${
                                  i === activeIndex ? 'bg-cafe-100/50 text-cafe-800' : 'text-cafe-700'
                                } ${selected ? 'font-medium' : ''}`}
                              >
                                {unit}
                                {selected && <Check className="h-4 w-4 text-cafe-700 flex-shrink-0" />}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    {unitError && <p className="mt-2 text-xs text-red-600">Seleccione una unidad</p>}
                  </div>
                </div>

                <div className="flex items-center justify-center gap-3 pt-3">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group flex items-center justify-center px-7 py-3 bg-cafe-700 rounded-xl text-white text-sm font-semibold tracking-wide shadow-[0_1px_2px_rgba(0,0,0,0.15),0_10px_24px_-10px_rgba(62,39,35,0.6)] hover:bg-cafe-800 hover:-translate-y-px hover:shadow-[0_1px_2px_rgba(0,0,0,0.15),0_14px_28px_-10px_rgba(62,39,35,0.7)] active:translate-y-0 active:scale-[0.99] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:scale-100"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2.5"></span>
                        Enviando...
                      </>
                    ) : (
                      <>
                        Enviar solicitud
                        <ArrowRight className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            /* Confirmación */
            <div className="text-center py-12 animate-card-in">
              <div className="w-14 h-14 rounded-full bg-neutral-50/50 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="h-7 w-7 text-cafe-700" />
              </div>
              <h3 className="text-2xl font-bold text-cafe-800 mb-2 tracking-tight">Solicitud enviada</h3>
              <p className="text-sm text-neutral-300 leading-relaxed max-w-xs mx-auto">
                Su solicitud fue enviada al equipo de TI. Le contactaremos por correo cuando esté lista.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialRequestModal;
